// @ts-nocheck

import React, { Fragment, useCallback, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { GridArrowLeft, GridArrowRight } from '../../assets/svg';
import { Target } from '../../client';
import theme from '../../styled-components/dark';
import { scrollToTop } from '../../utils';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  margin: 1em 0;
  padding-top: 1em;
  padding-bottom: 1em;
  color: ${theme.History.pagination.color};
`;

const WrapperNumbers = styled.div`
  display: flex;
  justify-content: center;
`;

const Number = styled.button`
  && {
    position: relative;
    color: ${({ $isHighlight }) => ($isHighlight ? '#fff' : theme.History.pagination.color)};
    background: transparent;
    border: none;
    cursor: pointer;
    &:focus {
      outline: 0;
    }
    &:active {
      transform: scale(0.9);
    }
    ${Target.__MOBILE__
      ? undefined
      : css`
          &:hover {
            color: #fff;
          }
        `}
  }
`;

const Button = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  width: 20%;
  height: 100%;
  padding: 0;
  position: absolute;
  top: 0;
  display: flex;
  justify-items: center;
  align-items: center;
  z-index: 1;
  text-align: center;
  &:focus {
    outline: 0;
  }
  &:active {
    transform: scale(0.9);
  }
`;

const flexCenterStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Previous = styled(Button)`
  left: 0;
  ${flexCenterStyle}
`;

const Next = styled(Button)`
  right: 0;
  ${flexCenterStyle}
`;

const arrowStyle = css`
  color: white;
  width: 1em;
  height: 1em;
  margin-top: -0.5em;
`;

const ArrowPrevious = styled(GridArrowLeft)`
  ${arrowStyle}
`;

const ArrowNext = styled(GridArrowRight)`
  ${arrowStyle}
`;

const SelectedBullet = styled.span`
  border-radius: 50%;
  width: 5px;
  height: 5px;
  background: ${theme.History.pagination.bulletColor};
  position: absolute;
  top: 100%;
  left: 50%;
  padding: 0;
  display: inline-block;
  transform: translate3d(-50%, 0, 0);
`;

type HistoryPaginationProps = {
  className?: string;
  nbPages: number;
  offset: number;
  setPage: (index: number) => void;
  nextPage: () => void;
  prevPage: () => void;
};

const createArray = (length) => Array.from({ length }, (_, i) => i);

const createArrayAroundOffset = (offset, nbPages) => {
  if (offset === 0 || offset === 1) {
    return [0, 1, 2, 3];
  } else if (offset === nbPages - 1 || offset === nbPages - 2) {
    return [nbPages - 4, nbPages - 3, nbPages - 2, nbPages - 1];
  }
  const result = [offset];
  if (offset - 1 > 0) {
    result.unshift(offset - 1);
  }
  if (offset - 2 > 0) {
    result.unshift(offset - 2);
  }
  if (offset + 1 < nbPages - 1) {
    result.push(offset + 1);
  }
  if (offset + 1 < nbPages - 2) {
    result.push(offset + 2);
  }
  return result;
};

const HistoryPagination = ({
  className,
  nbPages,
  offset,
  setPage = () => {},
  nextPage = () => {},
  prevPage = () => {},
}: HistoryPaginationProps) => {
  const finalOffset = useMemo(() => Math.max(Math.min(offset, nbPages - 1), 0), [offset, nbPages]);

  const renderPages = useCallback(
    (array) => {
      return array.map((index, arrayIndex) => (
        <Fragment key={index}>
          <Number $isHighlight={index === finalOffset} onClick={() => setPage(index)}>
            {index + 1}
            {index === finalOffset && <SelectedBullet />}
          </Number>
          {arrayIndex !== array.length - 1 ? '-' : null}
        </Fragment>
      ));
    },
    [finalOffset, setPage],
  );

  useEffect(() => {
    scrollToTop();
  }, [finalOffset]);

  if (nbPages > 0) {
    return (
      <Wrapper className={className}>
        {prevPage && finalOffset !== 0 && (
          <Previous onClick={prevPage}>
            <ArrowPrevious />
          </Previous>
        )}
        {nextPage && finalOffset !== nbPages - 1 && (
          <Next onClick={nextPage}>
            <ArrowNext />
          </Next>
        )}
        <WrapperNumbers>
          {nbPages <= 5 ? (
            <>{renderPages(createArray(nbPages))}</>
          ) : (
            <>
              {finalOffset > 1 && (
                <>
                  {renderPages([0])}
                  {finalOffset > 3 && ` ...`}
                </>
              )}
              {renderPages(createArrayAroundOffset(finalOffset, nbPages))}
              <>
                {finalOffset < nbPages - 4 && `... `}
                {finalOffset < nbPages - 2 && renderPages([nbPages - 1])}
              </>
            </>
          )}
        </WrapperNumbers>
      </Wrapper>
    );
  }
  return null;
};

export default HistoryPagination;
